<script setup lang="ts">
import merge from 'lodash-es/merge';
import Multiselect from 'vue-multiselect/src/Multiselect.vue';
import type { ListItemModel } from '#layers/elearning/app/pages/elearning/modules/courses/_runtime/services/CourseCategoriesService';
import CourseCategoriesService from '#layers/elearning/app/pages/elearning/modules/courses/_runtime/services/CourseCategoriesService';
import type { PageListItemModel } from '#layers/cms/app/pages/cms/management/pages/_runtime/services/PagesService';
import PagesService from '#layers/elearning/app/pages/cms/management/_runtime/services/PagesService';
import { useWebsitesStore } from '#layers/cms/app/pages/cms/management/websites/_runtime/stores/websites';

defineOptions({
    name: 'WidgetCoursesSettings'
});

const { $t } = useLocalization();
const { $log } = useLogging();

const emit = defineEmits<{
    (e: 'update:modelValue', payload: any): void;
}>();

const props = defineProps<{
    modelValue: any;
}>();

const layoutContext = inject<ComputedRef<{ websiteId: number; locale: string } | null>>('layoutPageSelectorContext', null);

const module = ref<string>('el_course_list');
const defaults = ref<any>({
    pages: [],
    redirectPage: null,
    display: 'Latest',
    categories: [],
    allCategories: true
});

const pageOptions = ref<PageListItemModel[]>([]);
const categoriesOptions = ref<ListItemModel[]>([]);
const optionsReady = ref(false);

const form = computed<FormType<any>>(() =>
{
    return props.modelValue;
});

const store = useWebsitesStore();

const displayOptions = computed((): Option[] =>
{
    return [
        { value: 'Latest', text: $t('[[[Najnowsze]]]') },
        { value: 'LastViewed', text: $t('[[[Ostatnio oglądane]]]') },
        { value: 'UpcomingEnd', text: $t('[[[Zbliżająca się data zakończenia]]]') }
    ];
});

const selectedPageOptions = computed((): Option[] =>
{
    return pageOptions.value.map((p) =>
    {
        return { value: p.id.toString(), text: p.option };
    });
});

const selectedCategoriesOptions = computed((): Option[] =>
{
    return categoriesOptions.value.map((p) =>
    {
        return { value: p.id.toString(), text: p.name };
    });
});

function effectiveWebsiteId(): number
{
    return layoutContext?.value?.websiteId ?? form.value.websiteId ?? store.current.website;
}

function effectiveLocale(): string
{
    return layoutContext?.value?.locale ?? form.value.locale ?? store.current.locale;
}

const triggetModel = (): void =>
{
    emit('update:modelValue', form.value);
};

const loadPages = async (): Promise<void> =>
{
    const websiteId = effectiveWebsiteId();
    const locale = effectiveLocale();

    if (!websiteId || !locale)
        return;

    try
    {
        pageOptions.value = await PagesService.getPagesByModule(websiteId, locale, module.value);
        pageOptions.value = pageOptions.value.filter(x => x.id != 0);
    }
    catch (ex)
    {
        pageOptions.value = [];
        $log.debug(ex);
    }
};

const loadCategories = async (): Promise<void> =>
{
    const websiteId = effectiveWebsiteId();
    const locale = effectiveLocale();

    if (!websiteId || !locale)
        return;

    try
    {
        categoriesOptions.value = await CourseCategoriesService.getOptions(websiteId, locale);
    }
    catch (ex)
    {
        categoriesOptions.value = [];
        $log.debug(ex);
    }
};

async function loadOptions(): Promise<void>
{
    optionsReady.value = false;

    await loadPages();
    await loadCategories();

    optionsReady.value = true;
}

watch(() => form.value.settings, () =>
{
    triggetModel();
}, { deep: true });

watch(() => [form.value.websiteId, form.value.locale, layoutContext?.value?.websiteId, layoutContext?.value?.locale], async () =>
{
    if (!effectiveWebsiteId() || !effectiveLocale())
        return;

    await loadOptions();
});

onBeforeMount(async (): Promise<void> =>
{
    form.value.settings = merge(defaults.value, form.value.settings);

    if (effectiveWebsiteId() && effectiveLocale())
    {
        await loadOptions();

        return;
    }

    await wait(() => effectiveWebsiteId() && effectiveLocale());
    await loadOptions();
});
</script>

<template>
    <IdeoFormGroup :label="$t('[[[Wybór listy stron]]]')" :invalid-feedback="form.$errors.first('settings.pages')" :state="form.$errors.state('settings.pages')" required>
        <Multiselect
            v-if="optionsReady"
            v-model="form.settings.pages"
            label="text"
            track-by="value"
            :options="selectedPageOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wybór strony przekierowującej]]]')" :invalid-feedback="form.$errors.first('settings.redirectPage')" :state="form.$errors.state('settings.redirectPage')">
        <Multiselect
            v-if="optionsReady"
            v-model="form.settings.redirectPage"
            label="text"
            track-by="value"
            :options="selectedPageOptions"
            :multiple="false"
            :placeholder="$t('[[[Wybierz stronę]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
        <template #description>
            <span>{{ $t('[[[Przycisk "zobacz wszystkie" znajdujący się przy bloczku, będzie przekierowywał na wybraną stronę z szkoleniami. Jeśli strona nie zostanie wybrana zostanie on ukryty.]]]') }}</span>
        </template>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Kategorie szkoleń]]]')" :invalid-feedback="form.$errors.first('source')" :state="form.$errors.state('source')">
        <IdeoFormRadioGroup class="mb-3">
            <IdeoFormRadio v-model="form.settings.allCategories" :value="true" name="selectCategories">
                {{ $t('[[[Wszystkie kategorie]]]') }}
            </IdeoFormRadio>
            <IdeoFormRadio v-model="form.settings.allCategories" :value="false" name="selectCategories">
                {{ $t('[[[Wybrane kategorie]]]') }}
            </IdeoFormRadio>
        </IdeoFormRadioGroup>
    </IdeoFormGroup>
    <IdeoFormGroup v-if="form.settings.allCategories == false" :label="$t('[[[Wybór kategorii]]]')" :invalid-feedback="form.$errors.first('settings.categories')" :state="form.$errors.state('settings.categories')">
        <Multiselect
            v-if="optionsReady"
            v-model="form.settings.categories"
            label="text"
            track-by="value"
            :options="selectedCategoriesOptions"
            :multiple="true"
            :placeholder="$t('[[[Wybierz strony]]]')"
        >
            <template #noOptions>
                {{ $t('[[[Lista jest pusta]]]') }}
            </template>
            <template #noResult>
                {{ $t('[[[Nie znaleziono żadnych wyników.]]]') }}
            </template>
        </Multiselect>
    </IdeoFormGroup>
    <IdeoFormGroup :label="$t('[[[Wyświetlanie]]]')" :invalid-feedback="form.$errors.first('settings.display')" :state="form.$errors.state('settings.display')">
        <IdeoFormSelect v-model="form.settings.display" :options="displayOptions" />
    </IdeoFormGroup>
</template>
